/**
 * CsvImportFlow — Shared CSV Import Component (EDG-531)
 *
 * Replaces the two divergent CSV import paths (top-bar Import CSV + Add FY wizard Step 3).
 * Implements Modano-style per-account mapping memory with interactive mapping preview.
 *
 * Flow: Upload → Parse + Delta Detection → Interactive Mapping Preview → Save Mappings → Bulk Import
 *
 * CRITICAL: Exposes defaultFinancialPeriodId prop for EDG-530 Wedge 1 pass-through.
 */

import { useState, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useBulkImport } from '@/hooks/use-financial-data';
import { parseCSV, toBulkImportPayload } from './csv-parser';
import type { ParsedCSVRow } from './csv-parser';
import { inferCategory } from '@shared/csv-categoriser-keywords';
import { getTemplateDownloadUrl, type FinancialPeriodOption } from './csv-import-wizard';
import {
  buildMappingsToSave,
  getDestinationOptions,
  materializeCustomImportRows,
} from './csv-import-flow-helpers';

// ============================================================================
// Types
// ============================================================================

/** Row status in the mapping preview */
export type MappingStatus = 'memory' | 'auto' | 'new' | 'unmap' | 'skip' | 'agg';

/** An enriched row with mapping metadata */
export interface MappedRow {
  index: number;
  sourceLabel: string;
  normalisedLabel: string;
  lineItemType: string;
  amount: number;
  status: MappingStatus;
  destinationKind: 'enum' | 'custom' | 'skip' | null;
  destinationEnumType: string | null;
  destinationCustomItemId: string | null;
  category: string;
  statementType: 'pl' | 'bs';
  inferredCategory: string | null;
  isNew: boolean;
  /** For auto-create: the label for the new custom line item */
  createNewLabel: string | null;
}

/** Period context for the import flow */
export type PeriodContext =
  | { mode: 'import-into-existing'; periodId: string; periodOptions: FinancialPeriodOption[] }
  | {
    mode: 'create-new-fy-then-import';
    newFyDraft: { label: string };
    ensureTargetPeriod: () => Promise<{ periodId: string; entityFinancialRecordId: string }>;
    onPeriodCreated: (id: string) => void;
  };

/** Props for the shared CsvImportFlow component */
export interface CsvImportFlowProps {
  accountId: string;
  entityFinancialRecordId?: string;
  periodContext: PeriodContext;
  /** Pre-fill the FY selector with this period ID (EDG-530 W1 pass-through) */
  defaultFinancialPeriodId?: string;
  onComplete: () => void;
  onCancel: () => void;
  /** Whether to show as a dialog (top-bar) or inline (wizard step) */
  asDialog?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type FlowStep = 'upload' | 'preview' | 'confirm-save' | 'importing' | 'success' | 'error';

// ============================================================================
// Status badge helper
// ============================================================================

const STATUS_BADGE_CONFIGS: Record<MappingStatus, { label: string; className: string }> = {
  memory: { label: 'Memory', className: 'bg-blue-100 text-blue-700' },
  auto: { label: 'Auto', className: 'bg-green-100 text-green-700' },
  new: { label: 'New \u26a0', className: 'bg-amber-100 text-amber-700' },
  unmap: { label: 'Unmap', className: 'bg-red-100 text-red-700' },
  skip: { label: 'Skip', className: 'bg-gray-100 text-gray-500' },
  agg: { label: '+Agg', className: 'bg-purple-100 text-purple-700' },
};

function StatusBadge({ status }: { status: MappingStatus }) {
  const cfg = STATUS_BADGE_CONFIGS[status];
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.className}`} data-testid={`csv-status-${status}`}>
      {cfg.label}
    </span>
  );
}

// ============================================================================
// Component
// ============================================================================

export function CsvImportFlow({
  accountId,
  entityFinancialRecordId,
  periodContext,
  defaultFinancialPeriodId,
  onComplete,
  onCancel,
  asDialog = true,
  open = true,
  onOpenChange,
}: CsvImportFlowProps) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkImport = useBulkImport();

  // State
  const [step, setStep] = useState<FlowStep>('upload');
  const [fileName, setFileName] = useState('');
  const [mappedRows, setMappedRows] = useState<MappedRow[]>([]);
  const [importResult, setImportResult] = useState<{ profitLoss: number; balanceSheet: number } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [savedMappings, setSavedMappings] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FY selector
  const defaultPeriodId = defaultFinancialPeriodId
    ?? (periodContext.mode === 'import-into-existing' ? periodContext.periodId : '');
  const [selectedPeriodId, setSelectedPeriodId] = useState(defaultPeriodId);

  // ============================================================================
  // Derived state
  // ============================================================================

  const unmappedRows = useMemo(
    () => mappedRows.filter(r =>
      r.status !== 'skip' && r.status !== 'memory' && r.status !== 'auto' && r.status !== 'agg'
      && r.destinationKind === null && !r.createNewLabel
    ),
    [mappedRows]
  );

  const importableRows = useMemo(
    () => mappedRows.filter(r =>
      r.status !== 'skip'
      && (r.destinationKind !== null || r.createNewLabel)
    ),
    [mappedRows]
  );

  const newMappings = useMemo(
    () => mappedRows.filter(r => r.isNew && r.status !== 'unmap' && r.status !== 'skip'),
    [mappedRows]
  );

  const aggregations = useMemo(() => {
    const destCounts = new Map<string, number>();
    for (const row of importableRows) {
      const key = row.destinationEnumType ?? row.createNewLabel ?? '';
      if (key) destCounts.set(key, (destCounts.get(key) ?? 0) + 1);
    }
    return destCounts;
  }, [importableRows]);

  const canImport = unmappedRows.length === 0;

  // ============================================================================
  // File handling
  // ============================================================================

  const handleFileSelect = useCallback(async (file: File) => {
    if (!file.name.endsWith('.csv')) {
      toast({ title: 'Invalid file type', description: 'Please select a CSV file (.csv)', variant: 'destructive' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum file size is 5 MB.', variant: 'destructive' });
      return;
    }

    try {
      const text = await file.text();
      const parsed = parseCSV(text);

      if (parsed.rows.length > 2000) {
        toast({ title: 'Too many rows', description: 'Maximum 2,000 rows per import.', variant: 'destructive' });
        return;
      }

      if (parsed.rows.length === 0) {
        toast({ title: 'No data found', description: 'The CSV file contains no valid financial data rows.', variant: 'destructive' });
        return;
      }

      // Fetch saved mappings for this account
      let accountMappings: Record<string, any> = {};
      try {
        const res = await fetch(`/api/ba/csv-mappings?accountId=${accountId}`);
        if (res.ok) {
          const mappings = await res.json();
          accountMappings = Object.fromEntries(
            mappings.map((m: any) => [m.sourceLabel, m])
          );
          setSavedMappings(accountMappings);
        }
      } catch {
        // Continue without saved mappings — first import
      }

      // Enrich rows with mapping status
      const enriched: MappedRow[] = parsed.rows.map((row: ParsedCSVRow, index: number) => {
        const normalised = (row.label || row.lineItemType).trim().toLowerCase();
        const saved = accountMappings[normalised];

        if (saved) {
          // Memory match
          if (saved.destinationKind === 'skip') {
            return {
              index,
              sourceLabel: row.label || row.lineItemType,
              normalisedLabel: normalised,
              lineItemType: row.lineItemType,
              amount: row.amount,
              status: 'skip' as MappingStatus,
              destinationKind: 'skip',
              destinationEnumType: null,
              destinationCustomItemId: null,
              category: saved.category,
              statementType: saved.statementType,
              inferredCategory: null,
              isNew: false,
              createNewLabel: null,
            };
          }
          return {
            index,
            sourceLabel: row.label || row.lineItemType,
            normalisedLabel: normalised,
            lineItemType: saved.destinationEnumType ?? row.lineItemType,
            amount: row.amount,
            status: 'memory' as MappingStatus,
            destinationKind: saved.destinationKind,
            destinationEnumType: saved.destinationEnumType,
            destinationCustomItemId: saved.destinationCustomItemId,
            category: saved.category,
            statementType: saved.statementType,
            inferredCategory: null,
            isNew: false,
            createNewLabel: null,
          };
        }

        if (row.matched) {
          // Auto-matched by parser
          return {
            index,
            sourceLabel: row.label || row.lineItemType,
            normalisedLabel: normalised,
            lineItemType: row.lineItemType,
            amount: row.amount,
            status: 'auto' as MappingStatus,
            destinationKind: 'enum',
            destinationEnumType: row.lineItemType,
            destinationCustomItemId: null,
            category: row.category === 'profitLoss' ? 'revenue' : 'current_asset',
            statementType: row.category === 'profitLoss' ? 'pl' : 'bs',
            inferredCategory: null,
            isNew: false,
            createNewLabel: null,
          };
        }

        // New/unmatched — infer category
        const inferred = inferCategory(normalised);
        return {
          index,
          sourceLabel: row.label || row.lineItemType,
          normalisedLabel: normalised,
          lineItemType: row.lineItemType,
          amount: row.amount,
          status: 'new' as MappingStatus,
          destinationKind: null,
          destinationEnumType: null,
          destinationCustomItemId: null,
          category: inferred.category,
          statementType: inferred.statementType,
          inferredCategory: inferred.category,
          isNew: true,
          createNewLabel: null,
        };
      });

      setMappedRows(enriched);
      setFileName(file.name);
      setStep('preview');
    } catch {
      toast({ title: 'Parse error', description: 'Failed to parse the CSV file. Please check the format.', variant: 'destructive' });
    }
  }, [accountId, toast]);

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // ============================================================================
  // Row actions
  // ============================================================================

  const updateRow = useCallback((index: number, updates: Partial<MappedRow>) => {
    setMappedRows(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  const handleDestinationChange = useCallback((rowIndex: number, value: string) => {
    if (value === '__skip__') {
      updateRow(rowIndex, {
        status: 'skip',
        destinationKind: 'skip',
        destinationEnumType: null,
        destinationCustomItemId: null,
        isNew: true,
      });
    } else if (value === '__create_new__') {
      const row = mappedRows[rowIndex];
      updateRow(rowIndex, {
        status: 'new',
        destinationKind: 'custom',
        destinationEnumType: null,
        createNewLabel: row.sourceLabel,
        isNew: true,
      });
    } else {
      // Standard enum destination
      const row = mappedRows[rowIndex];
      updateRow(rowIndex, {
        status: row.isNew ? 'new' : 'auto',
        destinationKind: 'enum',
        destinationEnumType: value,
        destinationCustomItemId: null,
        createNewLabel: null,
        isNew: !savedMappings[row.normalisedLabel],
      });
    }
  }, [mappedRows, updateRow, savedMappings]);

  const handleSkipAll = useCallback(() => {
    setMappedRows(prev => prev.map(row =>
      row.destinationKind === null && !row.createNewLabel
        ? { ...row, status: 'skip' as MappingStatus, destinationKind: 'skip', isNew: true }
        : row
    ));
  }, []);

  // ============================================================================
  // Import flow
  // ============================================================================

  const handleImport = useCallback(async (saveMappings: boolean) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    let targetPeriodId = selectedPeriodId;
    let targetEntityFinancialRecordId = entityFinancialRecordId;

    // For create-new-fy-then-import mode, create the FY period first
    if (!targetPeriodId && periodContext.mode === 'create-new-fy-then-import') {
      try {
        const created = await periodContext.ensureTargetPeriod();
        targetPeriodId = created.periodId;
        targetEntityFinancialRecordId = created.entityFinancialRecordId;
        setSelectedPeriodId(targetPeriodId);
        periodContext.onPeriodCreated(targetPeriodId);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to create financial year';
        setErrorMessage(message);
        setStep('error');
        toast({ title: 'FY creation failed', description: message, variant: 'destructive' });
        return;
      }
    }

    if (!targetPeriodId) return;

    setStep('importing');

    try {
      let materializedRows = mappedRows;

      const hasCustomRows = materializedRows.some(r => r.createNewLabel && !r.destinationCustomItemId);
      if (hasCustomRows) {
        if (!targetEntityFinancialRecordId) {
          throw new Error('Failed to prepare entity record');
        }

        materializedRows = await materializeCustomImportRows({
          rows: materializedRows,
          entityFinancialRecordId: targetEntityFinancialRecordId,
          financialPeriodId: targetPeriodId,
          deps: {
            createCustomLineItem: async (input) => {
              const response = await fetch('/api/financial/custom-line-items', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(input),
              });
              if (!response.ok) {
                throw new Error('Failed to create custom line item');
              }
              const data = await response.json();
              return data.data ?? data;
            },
            upsertCustomLineItemValue: async (input) => {
              const response = await fetch('/api/financial/custom-line-item-values/upsert', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  customLineItemId: input.customLineItemId,
                  financialPeriodId: input.financialPeriodId,
                  amount: String(input.amount),
                }),
              });
              if (!response.ok) {
                throw new Error('Failed to save custom line item value');
              }
            },
          },
        });
        setMappedRows(materializedRows);
      }

      // Phase 1: Save mappings if requested
      if (saveMappings && newMappings.length > 0) {
        const mappingsToSave = buildMappingsToSave(materializedRows, accountId);

        if (mappingsToSave.length > 0) {
          const res = await fetch('/api/ba/csv-mappings', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mappings: mappingsToSave }),
          });
          if (!res.ok) {
            throw new Error('Failed to save mappings');
          }
        }
      }

      // Phase 2: Build payload from importable rows
      const rowsForImport = materializedRows
        .filter(r => r.destinationKind === 'enum')
        .map(r => ({
        lineItemType: r.destinationEnumType ?? r.lineItemType,
        label: r.sourceLabel,
        amount: r.amount,
        matched: true,
        category: r.statementType === 'pl' ? 'profitLoss' as const : 'balanceSheet' as const,
      }));

      const payload = toBulkImportPayload(rowsForImport);

      // Phase 3: Bulk import (with cache invalidation via useBulkImport)
      const body = await bulkImport.mutateAsync({ periodId: targetPeriodId, payload });

      setImportResult({
        profitLoss: body.created.profitLoss,
        balanceSheet: body.created.balanceSheet,
      });
      setStep('success');

      toast({
        title: 'Import successful',
        description: `Imported ${body.created.profitLoss} P&L items and ${body.created.balanceSheet} BS items`,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Import failed';
      setErrorMessage(message);
      setStep('error');
      setIsSubmitting(false);
      toast({ title: 'Import failed', description: message, variant: 'destructive' });
    }
  }, [accountId, bulkImport, entityFinancialRecordId, isSubmitting, mappedRows, newMappings, periodContext, selectedPeriodId, toast]);

  const handleImportClick = useCallback(() => {
    if (isSubmitting) return;
    if (newMappings.length > 0) {
      setStep('confirm-save');
    } else {
      handleImport(false);
    }
  }, [handleImport, isSubmitting, newMappings]);

  // ============================================================================
  // Reset
  // ============================================================================

  const resetState = useCallback(() => {
    setStep('upload');
    setMappedRows([]);
    setFileName('');
    setImportResult(null);
    setErrorMessage('');
    setSelectedPeriodId(defaultPeriodId);
    setSavedMappings({});
    setIsSubmitting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [defaultPeriodId]);

  const handleClose = useCallback(() => {
    resetState();
    onCancel();
    onOpenChange?.(false);
  }, [resetState, onCancel, onOpenChange]);

  const handleDone = useCallback(() => {
    onComplete();
    onOpenChange?.(false);
  }, [onComplete, onOpenChange]);

  // ============================================================================
  // Render content
  // ============================================================================

  const content = (
    <>
      {step === 'upload' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <a
              href={getTemplateDownloadUrl(accountId)}
              download="edgeview-financial-template.csv"
              className="text-sm text-blue-600 hover:text-blue-800 underline"
              data-testid="download-template-link"
            >
              Download Template
            </a>
          </div>
          <div
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-gray-400 transition-colors"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onClick={() => fileInputRef.current?.click()}
            data-testid="csv-dropzone"
          >
            <p className="text-gray-500">Drop CSV file here or click to browse</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFileSelect(f); }}
              data-testid="csv-file-input"
            />
          </div>
        </div>
      )}

      {step === 'preview' && (
        <div className="space-y-4">
          {/* FY Period Selector */}
          {periodContext.mode === 'import-into-existing' && periodContext.periodOptions.length > 0 && (
            <div className="flex items-center gap-2">
              <label htmlFor="csv-fy-select" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Import into FY:
              </label>
              <select
                id="csv-fy-select"
                className="text-sm border rounded px-2 py-1 flex-1"
                value={selectedPeriodId}
                onChange={(e) => setSelectedPeriodId(e.target.value)}
                data-testid="csv-fy-select"
              >
                {periodContext.periodOptions.map((p) => (
                  <option key={p.id} value={p.id}>{p.label}</option>
                ))}
              </select>
            </div>
          )}

          {/* Summary bar */}
          <div className="text-sm text-gray-600" data-testid="csv-summary">
            File: <span className="font-medium">{fileName}</span> |{' '}
            {mappedRows.length} rows |{' '}
            {mappedRows.filter(r => r.status === 'memory').length} memory |{' '}
            {mappedRows.filter(r => r.status === 'auto').length} auto |{' '}
            {mappedRows.filter(r => r.isNew).length} new
          </div>

          {/* Warning banner */}
          {unmappedRows.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between" data-testid="csv-unmapped-banner">
              <span className="text-sm text-amber-700">
                ⚠ {unmappedRows.length} row{unmappedRows.length === 1 ? '' : 's'} need{unmappedRows.length === 1 ? 's' : ''} a destination before import
              </span>
              <Button variant="outline" size="sm" onClick={handleSkipAll} data-testid="csv-skip-all-button">
                Skip all {unmappedRows.length}
              </Button>
            </div>
          )}

          {/* Preview table */}
          <div className="border rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="csv-preview-table">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="border-b">
                  <th className="px-3 py-2 text-left font-medium">Source Label</th>
                  <th className="px-3 py-2 text-left font-medium">Destination</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-center font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {mappedRows.map((row) => {
                  const aggCount = row.destinationEnumType ? (aggregations.get(row.destinationEnumType) ?? 1) : 1;
                  const displayStatus: MappingStatus = aggCount > 1 && row.status !== 'skip' ? 'agg' : row.status;

                  return (
                    <tr
                      key={row.index}
                      className={`border-b ${row.status === 'unmap' || row.status === 'new' ? 'bg-amber-50' : ''} ${row.status === 'skip' ? 'opacity-50' : ''}`}
                      data-testid={`csv-row-${row.index}`}
                    >
                      <td className="px-3 py-2">
                        <span className={row.isNew ? 'text-amber-700' : ''}>{row.sourceLabel}</span>
                      </td>
                      <td className="px-3 py-2">
                        {row.status === 'memory' || row.status === 'auto' ? (
                          <select
                            className="text-xs border rounded px-1 py-0.5 w-full"
                            value={row.destinationEnumType ?? '__skip__'}
                            onChange={(e) => handleDestinationChange(row.index, e.target.value)}
                            data-testid={`csv-dest-select-${row.index}`}
                          >
                            {getDestinationOptions(row.statementType).map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                            <option value="__skip__">Skip this row</option>
                          </select>
                        ) : row.status === 'skip' ? (
                          <span className="text-gray-400 text-xs">Skipped</span>
                        ) : (
                          <div className="space-y-1">
                            <select
                              className="text-xs border rounded px-1 py-0.5 w-full border-amber-300"
                              value={row.destinationEnumType ?? (row.createNewLabel ? '__create_new__' : '')}
                              onChange={(e) => handleDestinationChange(row.index, e.target.value)}
                              data-testid={`csv-dest-select-${row.index}`}
                            >
                              <option value="">— choose destination —</option>
                              <option value="__create_new__">🆕 Create &quot;{row.sourceLabel}&quot;</option>
                              <option value="__skip__">Skip this row</option>
                              <option disabled>──────────</option>
                              {getDestinationOptions(row.statementType).map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {row.createNewLabel && (
                              <div className="text-xs text-gray-500 pl-1">
                                Category: <span className="font-medium">{row.category}</span> ({row.statementType.toUpperCase()})
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {new Intl.NumberFormat('en-AU', {
                          style: 'currency',
                          currency: 'AUD',
                          minimumFractionDigits: 0,
                        }).format(row.amount)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <StatusBadge status={displayStatus} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center pt-2">
            <Button variant="outline" onClick={resetState} data-testid="csv-back-button">
              Back
            </Button>
            <Button
              onClick={handleImportClick}
              disabled={!canImport}
              data-testid="csv-import-button"
            >
              Import {importableRows.length} of {mappedRows.length} →
            </Button>
          </div>
        </div>
      )}

      {step === 'confirm-save' && (
        <div className="py-6 space-y-4 text-center" data-testid="csv-confirm-save">
          <p className="text-sm font-medium">Save mappings?</p>
          <p className="text-sm text-gray-600">
            {newMappings.length} new mapping{newMappings.length === 1 ? '' : 's'} will be saved to this account&apos;s mapping file.
            They&apos;ll apply automatically on your next import for this account.
          </p>
          <div className="flex justify-center gap-3 pt-2">
            <Button onClick={() => handleImport(true)} disabled={isSubmitting} data-testid="csv-save-import-button">
              {isSubmitting ? 'Importing\u2026' : 'Save + Import'}
            </Button>
            <Button variant="outline" onClick={() => handleImport(false)} disabled={isSubmitting} data-testid="csv-import-no-save-button">
              Import without saving
            </Button>
          </div>
        </div>
      )}

      {step === 'importing' && (
        <div className="py-8 text-center" data-testid="csv-importing-state">
          <p className="text-gray-500">Importing financial data...</p>
        </div>
      )}

      {step === 'success' && importResult && (
        <div className="py-8 text-center space-y-4" data-testid="csv-success-state">
          <p className="text-green-600 font-medium">Import complete</p>
          <p className="text-sm text-gray-600">
            Imported {importResult.profitLoss} P&L items and {importResult.balanceSheet} BS items
          </p>
          <Button onClick={handleDone} data-testid="csv-done-button">Done</Button>
        </div>
      )}

      {step === 'error' && (
        <div className="py-8 text-center space-y-4" data-testid="csv-error-state">
          <p className="text-red-600 font-medium">Import failed</p>
          <p className="text-sm text-gray-600">{errorMessage}</p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={resetState} data-testid="csv-retry-button">Try Again</Button>
            <Button onClick={handleClose}>Close</Button>
          </div>
        </div>
      )}
    </>
  );

  // ============================================================================
  // Render — Dialog vs Inline
  // ============================================================================

  if (asDialog) {
    return (
      <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="csv-import-flow">
          <DialogHeader>
            <DialogTitle>CSV/Excel Import — Preview</DialogTitle>
            <DialogDescription>
              Import financial data from a CSV file with interactive mapping
            </DialogDescription>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return <div data-testid="csv-import-flow-inline">{content}</div>;
}
